import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import WaitlistForm from "@/components/WaitlistForm";
import { Mail, Sprout, Users, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";
import { useSiteContent } from "@/context/SiteContentContext";

export default function HomePage() {
  const [growers, setGrowers] = useState([]);
  const [areaTotals, setAreaTotals] = useState(null);
  const { t } = useSiteContent();

  useEffect(() => {
    api.get("/public/growers").then(({ data }) => setGrowers(data.growers || []));
    api.get("/public/area-map").then(({ data }) => setAreaTotals(data.totals || {}));
  }, []);

  const featuredGrowers = growers.slice(0, 3);
  const totalWaitlist = areaTotals?.total_waitlist_teaser || 0;
  const totalRegions = (areaTotals?.open || 0) + (areaTotals?.pending || 0);

  return (
    <div className="club-theme min-h-screen" data-testid="home-page">
      <PublicHeader />

      {/* ===== HERO ===== */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-10 text-center" data-testid="hero-section">
        <img
          src={BRAND_ASSETS.banner}
          alt="Heritage Bloom Club Est 2026"
          className="w-full object-contain mx-auto"
          style={{ filter: "drop-shadow(0 14px 36px rgba(61,31,71,0.18))" }}
        />

        <h1 className="font-serif italic text-3xl md:text-5xl leading-[1.1] mt-12 md:mt-16 px-2" data-testid="hero-headline">
          {t("home.hero.headline", "A small, considered subscription for people who love seasonal flowers, dahlias and the slow craft of growing.")}
        </h1>

        <div className="mt-12 md:mt-14 flex flex-col gap-4 max-w-md mx-auto" data-testid="hero-ctas">
          <Link to="/waitlist" className="bloom-btn bloom-btn-primary justify-center" data-testid="hero-waitlist-cta">
            <Mail size={16} strokeWidth={1.5} />
            <span>{t("home.hero.cta_primary", "Join the Waitlist")}</span>
            <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
          <Link to="/become-a-grower" className="bloom-btn bloom-btn-outline justify-center" data-testid="hero-grower-cta">
            <Sprout size={16} strokeWidth={1.5} />
            <span>{t("home.hero.cta_secondary", "Become a Grower")}</span>
          </Link>
        </div>

        <div className="mt-10 flex items-center justify-center gap-8 md:gap-12 text-sm" data-testid="hero-secondary-links">
          <Link to="/growers" className="club-link inline-flex items-center gap-2">
            <Users size={14} strokeWidth={1.5} /> Meet our growers
          </Link>
          <Link to="/territories" className="club-link inline-flex items-center gap-2">
            <MapPin size={14} strokeWidth={1.5} /> Territory map
          </Link>
        </div>

        <div className="mt-10 inline-flex flex-wrap items-center justify-center" data-testid="hero-stats">
          <div className="club-stat-pill">
            <div className="flex items-center gap-3">
              <Users size={14} strokeWidth={1.5} />
              <span className="font-serif text-xl">{totalWaitlist}</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">on the waitlist</span>
            </div>
            <div className="club-stat-divider" />
            <div className="flex items-center gap-3">
              <MapPin size={14} strokeWidth={1.5} />
              <span className="font-serif text-xl">{totalRegions}</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">{totalRegions === 1 ? "region" : "regions"}</span>
            </div>
          </div>
        </div>

        <div className="mt-14 flex justify-center" data-testid="hero-divider">
          <FlowerDivider />
        </div>
      </section>

      {/* ===== MANIFESTO ===== */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center" data-testid="manifesto-section">
        <div className="bloom-label mb-4">✦ The Manifesto ✦</div>
        <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.2] mb-10">
          The Heritage Bloom Club isn&rsquo;t just a flower subscription. It&rsquo;s a way of reconnecting people with local flower farms.
        </h2>
        <div className="space-y-6 font-serif text-lg text-[var(--bloom-ink-soft)] leading-[1.85] text-left">
          <p>
            Customers sign up to receive a seasonal bloom box filled with freshly picked dahlias and complementary flowers,
            all grown by farmers in their own region. The flowers arrive flat laid and unarranged, allowing customers to create
            something beautiful themselves at home. Part flower delivery, part creative experience, part connection to the seasons.
          </p>
          <p>
            What makes the idea unique is that it doesn&rsquo;t rely on flowers being shipped around the country.
            Local flower farms service their own communities. Heritage Dahlias handles the brand, customer experience and subscriptions,
            while participating growers harvest, pack and deliver from their own farms.
          </p>
          <p className="italic text-center text-[var(--bloom-ink)]">
            Local flowers, grown by local people, delivered fresh, and enjoyed in a way that brings a little
            creativity and beauty into everyday life.
          </p>
        </div>
        <div className="mt-12 flex justify-center"><FlowerDivider /></div>
      </section>

      {/* ===== WHY (3 pillars) ===== */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-16" data-testid="why-section">
        <div className="text-center mb-12">
          <div className="bloom-label mb-3">✦ Why we do this ✦</div>
          <h2 className="font-serif italic text-3xl md:text-4xl">A different way to do flowers.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <Pillar n="I" title="Local to local"
            body="Local flower farms service their own communities. No long haul shipping, fresher blooms." />
          <Pillar n="II" title="Creative + connected"
            body="Flat-laid and unarranged. You make something beautiful yourself, the way it used to be." />
          <Pillar n="III" title="Seasonal + honest"
            body="Every box reflects what's actually blooming in your region right now." />
        </div>
      </section>

      {/* ===== GROWERS PREVIEW ===== */}
      {featuredGrowers.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-16" data-testid="growers-preview-section">
          <div className="text-center mb-12">
            <div className="bloom-label mb-3">✦ Our network ✦</div>
            <h2 className="font-serif italic text-3xl md:text-4xl">{t("home.section.growers.heading", "Meet our growers.")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredGrowers.map((g) => (
              <GrowerCardMini key={g.id} grower={g} />
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/growers" className="bloom-btn bloom-btn-outline" data-testid="see-all-growers">
              See all growers <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* ===== WAITLIST ===== */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-16" data-testid="waitlist-section">
        <div className="text-center mb-10">
          <div className="bloom-label mb-3">✦ Summer 2027 ✦</div>
          <h2 className="font-serif italic text-3xl md:text-4xl mb-4">{t("home.section.waitlist.heading", "Join the waitlist.")}</h2>
          <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] max-w-xl mx-auto">
            {t("home.section.waitlist.sub", "Pop your details below and we'll let you know the moment your area opens up. Boxes are limited per region.")}
          </p>
        </div>
        <WaitlistForm />
      </section>

      {/* ===== CLOSING ===== */}
      <section className="py-24 text-center" data-testid="closing-section">
        <div className="max-w-2xl mx-auto px-5 md:px-8">
          <Sparkles className="mx-auto mb-6 text-[var(--bloom-accent)]" strokeWidth={1.2} size={32} />
          <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.2]">
            One season. One region.<br/>One bloom box at a time.
          </h2>
          <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] mt-5">
            We&rsquo;re building this carefully. Together, slowly, beautifully.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

// ---------- small atoms ----------
function FlowerDivider() {
  return (
    <div className="club-flower-divider max-w-sm" data-testid="flower-divider">
      <span>✦</span>
    </div>
  );
}

function Pillar({ n, title, body }) {
  return (
    <div className="text-center md:text-left">
      <div className="font-serif italic text-3xl text-[var(--bloom-accent)] mb-3">{n}</div>
      <h3 className="font-serif text-2xl leading-tight mb-3">{title}</h3>
      <p className="font-serif text-base text-[var(--bloom-ink-soft)] leading-relaxed italic">{body}</p>
    </div>
  );
}

function GrowerCardMini({ grower }) {
  return (
    <Link to="/growers" className="block group" data-testid={`grower-mini-${grower.id}`}>
      <div className="aspect-[4/5] overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
        {grower.photo ? (
          <img src={grower.photo} alt={grower.farm_name} className="w-full h-full object-cover group-hover:scale-[1.02] transition duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--bloom-ink-muted)] italic font-serif">{grower.farm_name}</div>
        )}
      </div>
      <div className="mt-4 text-center">
        <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mb-1">
          <MapPin size={11} /> {grower.region || ""}
        </div>
        <div className="font-serif text-2xl italic leading-tight">{grower.farm_name}</div>
        <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">{grower.name}</div>
      </div>
    </Link>
  );
}
